/**
 * Teen Driving Safety (§22.20) — parent view.
 *
 * Privacy by design: parents see trip *aggregates* + safety incidents
 * (speeding, harsh braking) and a weekly report — never turn-by-turn route
 * playback (that's the teen's own view). Enable per teen, set the speed-alert
 * threshold, and recommend Do-Not-Disturb-While-Driving.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Car, Gauge, ShieldAlert } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { usersApi } from '@/api/users';
import { drivingApi } from '@/api/driving';
import { ChildPicker } from './ChildPicker';
import { PageHeader } from './PageHeader';
import { EmptyState } from './EmptyState';

function scoreColor(score: number): string {
  if (score >= 85) return 'bg-emerald-100 text-emerald-800';
  if (score >= 65) return 'bg-amber-100 text-amber-800';
  return 'bg-rose-100 text-rose-800';
}

export function DrivingPage() {
  const { t } = useTranslation();
  const children = useQuery({ queryKey: ['children'], queryFn: () => usersApi.listChildren() });
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);

  return (
    <div className="px-6 py-8 md:px-10 md:py-10">
      <PageHeader
        title={t('pages.driving.title')}
        icon={Car}
        tint="amber"
        description={t('pages.driving.description')}
      />

      <div className="mt-6">
        <ChildPicker children={children.data ?? []} selectedId={selectedChildId} onSelect={setSelectedChildId} />
      </div>

      {!selectedChildId ? (
        <Card className="mt-6">
          <CardContent className="p-8 text-center text-sm text-[color:var(--kz-color-ink-secondary)]">
            {children.data?.length === 0 ? 'Add a child first.' : 'Select a teen.'}
          </CardContent>
        </Card>
      ) : (
        <div className="mt-6 grid gap-4 lg:grid-cols-[20rem_1fr]">
          <div className="space-y-4">
            <SettingsCard childId={selectedChildId} />
            <ReportCard childId={selectedChildId} />
          </div>
          <TripsCard childId={selectedChildId} />
        </div>
      )}
    </div>
  );
}

function SettingsCard({ childId }: { childId: string }) {
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ['driving', 'settings', childId],
    queryFn: () => drivingApi.settings(childId),
  });
  const [limit, setLimit] = useState<string>('');
  const [hydrated, setHydrated] = useState(false);
  if (settings.data && !hydrated) {
    setLimit(String(settings.data.speed_limit_kmh));
    setHydrated(true);
  }

  const update = useMutation({
    mutationFn: (input: Partial<{ enabled: boolean; speed_limit_kmh: number; dnd_recommended: boolean }>) =>
      drivingApi.updateSettings(childId, input),
    onSuccess: () => {
      toast.success('Saved.');
      void queryClient.invalidateQueries({ queryKey: ['driving', 'settings', childId] });
    },
    onError: () => toast.error('Save failed.'),
  });

  const s = settings.data;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Car className="size-4" /> Driving mode
        </CardTitle>
        <CardDescription>Enable for teens with a learner permit or licence.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Enabled</span>
          <Switch
            checked={s?.enabled ?? false}
            onCheckedChange={(v) => update.mutate({ enabled: v })}
            disabled={update.isPending}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="speed">Speed alert threshold (km/h)</Label>
          <div className="flex gap-2">
            <Input id="speed" type="number" min={30} max={200} value={limit} onChange={(e) => setLimit(e.target.value)} />
            <Button size="sm" variant="outline" onClick={() => update.mutate({ speed_limit_kmh: parseInt(limit, 10) || 110 })} disabled={update.isPending}>
              Save
            </Button>
          </div>
        </div>
        <div className="flex items-center justify-between rounded-md border border-[color:var(--kz-color-border-subtle)] p-3">
          <div>
            <p className="text-sm font-medium">Do Not Disturb while driving</p>
            <p className="text-xs text-[color:var(--kz-color-ink-muted)]">Recommend the device silences notifications on the road.</p>
          </div>
          <Switch
            checked={s?.dnd_recommended ?? true}
            onCheckedChange={(v) => update.mutate({ dnd_recommended: v })}
            disabled={update.isPending}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function ReportCard({ childId }: { childId: string }) {
  const report = useQuery({
    queryKey: ['driving', 'report', childId],
    queryFn: () => drivingApi.report(childId, 7),
  });
  const r = report.data;
  if (!r) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">This week</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-3">
        <Metric value={`${r.trips}`} label="trips" />
        <Metric value={`${r.total_distance_km}`} label="km driven" />
        <Metric value={`${r.total_speed_alerts}`} label="speed alerts" />
        <Metric value={`${r.total_harsh_events}`} label="harsh events" />
        <div className="col-span-2 rounded-card bg-[color:var(--kz-color-surface-sunken)] p-3 text-center">
          <p className="text-3xl font-semibold text-[color:var(--kz-color-brand-primary)]">{r.avg_safety_score}</p>
          <p className="text-[11px] text-[color:var(--kz-color-ink-muted)]">average safety score</p>
        </div>
      </CardContent>
    </Card>
  );
}

function Metric({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-card bg-[color:var(--kz-color-surface-sunken)] p-2.5 text-center">
      <p className="text-lg font-semibold tabular-nums">{value}</p>
      <p className="text-[11px] text-[color:var(--kz-color-ink-muted)]">{label}</p>
    </div>
  );
}

function TripsCard({ childId }: { childId: string }) {
  const trips = useQuery({
    queryKey: ['driving', 'trips', childId],
    queryFn: () => drivingApi.trips(childId, 30),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Recent trips</CardTitle>
        <CardDescription>Aggregates + safety incidents only — the route playback stays private to your teen.</CardDescription>
      </CardHeader>
      <CardContent>
        {(trips.data ?? []).length === 0 ? (
          <EmptyState
            icon={Car}
            tint="amber"
            title="No trips recorded yet"
            description="Enable driving safety on a teen device — trip aggregates and incidents will appear here."
          />
        ) : (
          <ul className="space-y-2">
            {(trips.data ?? []).map((t) => (
              <li key={t.id} className="rounded-card border border-[color:var(--kz-color-border-subtle)] p-3">
                <div className="flex items-center gap-3">
                  <span className={`grid size-10 shrink-0 place-items-center rounded-full text-sm font-semibold ${scoreColor(t.safety_score)}`}>
                    {t.safety_score}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">
                      {new Date(t.started_at).toLocaleString()}
                    </p>
                    <p className="text-xs text-[color:var(--kz-color-ink-muted)]">
                      {(t.distance_m / 1000).toFixed(1)} km · {Math.round(t.duration_s / 60)} min · max {Math.round(t.max_speed_kmh)} km/h
                    </p>
                  </div>
                  {t.speed_alert_count > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-xs text-rose-800">
                      <Gauge className="size-3" /> {t.speed_alert_count}
                    </span>
                  )}
                  {t.harsh_event_count > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                      <ShieldAlert className="size-3" /> {t.harsh_event_count}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
